import React from 'react'
import PropTypes from 'prop-types'
import { useTable, useFlexLayout, usePagination } from 'react-table'
import { Center, Box, Flex, Text, ButtonGroup, Button, IconButton, Icon } from '@chakra-ui/react'
import FiChevronLeft from '@meronex/icons/fi/FiChevronLeft'
import FiChevronRight from '@meronex/icons/fi/FiChevronRight'

import { Table, Tbody, Td, Th, Thead, Tr } from '../table-core'

// sample code from:
// 1. https://codesandbox.io/s/chakra-ui-react-table-o6psn
// 2. https://codesandbox.io/s/chakra-ui-react-table-light-6ikg9
// which is from this comment:
// 1. https://github.com/chakra-ui/chakra-ui/issues/135#issuecomment-590827734
// 2. https://github.com/chakra-ui/chakra-ui/issues/135#issuecomment-623340352
// This component could be updated if this issue has been closed: https://github.com/chakra-ui/chakra-ui/issues/135
export const DataTable = ({ columns, data, total, initialPagination, isManual, isLoading, onFetchData, loadingContent, emptyContent }) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    canPreviousPage,
    canNextPage,
    page,
    gotoPage,
    nextPage,
    previousPage,
    pageOptions,
    pageCount,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: initialPagination.index, pageSize: initialPagination.size },
      pageCount: isManual ? Math.ceil(total / initialPagination.size) : undefined,
      manualPagination: isManual,
    },
    useFlexLayout,
    usePagination,
  )

  const onPageChange = React.useCallback((event) => gotoPage(Number(event.target.dataset.index)), [gotoPage])

  const rowRange = React.useMemo(
    () => ({ start: total > 0 ? pageIndex * pageSize + 1 : 0, end: total > (pageIndex + 1) * pageSize ? (pageIndex + 1) * pageSize : total }),
    [total, pageIndex, pageSize],
  )
  const pageRange = React.useMemo(
    () => ({
      start: pageCount > 5 && pageIndex >= 3 ? (pageCount - pageIndex > 3 ? pageIndex - 2 : pageCount - 5) : 0,
      end: pageCount > 5 && pageIndex >= 3 ? (pageCount - pageIndex > 3 ? pageIndex + 3 : pageCount) : 5,
    }),
    [pageCount, pageIndex],
  )

  React.useEffect(() => {
    isManual && onFetchData({ pageIndex, pageSize })
  }, [isManual, onFetchData, pageIndex, pageSize])

  return (
    <Flex position='relative' direction='column' boxSize='100%' overflow='hidden'>
      {/* wrap table to display scroll bar without pagination */}
      <Box flex='1' width='100%' overflow='auto'>
        <Table {...getTableProps()}>
          <Thead
            zIndex='2'
            position='relative' // make sure content in table's body won't overflow table's header
          >
            {headerGroups.map((headerGroup, index) => (
              <Tr key={index} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, index) => (
                  <Th key={index} {...column.headerStyle} {...column.getHeaderProps()}>
                    {column.render('Header')}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>

          <Tbody {...getTableBodyProps()}>
            {page.length > 0
              ? page.map((row, index) => {
                  prepareRow(row)

                  return (
                    <Tr key={index} {...row.getRowProps()}>
                      {row.cells.map((cell, index) => (
                        <Td key={index} {...cell.column.style} {...cell.getCellProps()}>
                          {cell.render('Cell')}
                        </Td>
                      ))}
                    </Tr>
                  )
                })
              : emptyContent}
          </Tbody>
        </Table>
      </Box>

      <Flex padding='1.75rem 2.5rem' align='center' justify='space-between'>
        <Text color='gray.500' fontSize='xs'>
          {`${rowRange.start} - ${rowRange.end} out of ${total}`}
        </Text>

        <ButtonGroup spacing='0.25rem' size='md' variant='ghost'>
          <IconButton
            aria-label='previous-page'
            icon={<Icon as={FiChevronLeft} boxSize='1rem' />}
            isDisabled={!canPreviousPage}
            onClick={previousPage}
          />
          {pageOptions.slice(pageRange.start, pageRange.end).map((option, index) => (
            <Button
              key={index}
              data-index={option}
              isActive={option === pageIndex}
              _active={{ color: 'white', bgColor: 'brand.500' }}
              onClick={onPageChange}
            >
              {option + 1}
            </Button>
          ))}
          <IconButton aria-label='next-page' icon={<Icon as={FiChevronRight} boxSize='1rem' />} isDisabled={!canNextPage} onClick={nextPage} />
        </ButtonGroup>
      </Flex>

      {isLoading && loadingContent}
    </Flex>
  )
}

DataTable.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  total: PropTypes.number,
  initialPagination: PropTypes.shape({ index: PropTypes.number, size: PropTypes.number }),
  isManual: PropTypes.bool,
  isLoading: PropTypes.bool,
  onFetchData: PropTypes.func,
  pageCount: PropTypes.number,
  loadingContent: PropTypes.node,
  emptyContent: PropTypes.node,
}

DataTable.defaultProps = {
  initialPagination: { size: 10 },
  isManual: false,
  pageCount: -1,
  loadingContent: (
    <Center zIndex='1' position='absolute' top='0' right='0' bottom='0' left='0' fontSize='sm' color='gray.700' bgColor='whiteAlpha.600'>
      Loading...
    </Center>
  ),
  emptyContent: (
    <Text paddingY='5rem' textAlign='center' fontSize='sm' color='gray.700'>
      No results found
    </Text>
  ),
}
